/**
 * Linking a pharmacy dispense transaction back to the CPOE order it fills.
 *
 * There is no foreign key to use. `source_reference_id` is only sent when the
 * prescription id is a uuid, and IPD order ids never are, so the TRANSACTION-level
 * link is the remarks string StartDispensePage writes: "Dispense for ORD-…".
 * Fragile, but it is what exists — see buildCreateDispenseBody for why
 * transaction_number cannot be used instead (it is server-generated as DISP-…,
 * deliberately, to avoid 409s).
 *
 * Per-LINE attribution uses `item_payload` instead (see buildOrderLinkPayload
 * below), which the dispensing service persists and echoes back verbatim.
 *
 * Import-free so the matching logic stays unit-testable (same reasoning as
 * prescriptionStatus / prescriptionMedicine: anything reaching api/inventory-request
 * pulls in axios ESM, which Jest cannot load).
 */

/** Statuses that mean the pharmacy is done with the transaction. */
const CLOSED_DISPENSE_STATUSES = ['DISPENSED', 'COMPLETED', 'CANCELLED', 'REVERSED'];

/** The CPOE order a dispense was created for, or '' when it cannot be resolved. */
export function dispenseOrderNo(dispense) {
  if (!dispense || typeof dispense !== 'object') return '';
  const fromRemarks = String(dispense.remarks || '').match(/Dispense for\s+(\S+)/i);
  if (fromRemarks?.[1]) return fromRemarks[1];
  return dispense.transaction_number ? String(dispense.transaction_number) : '';
}

export function dispenseId(dispense) {
  return dispense?.transaction_id || dispense?.id || '';
}

/**
 * Still in flight — this is the transaction a pharmacist should continue.
 *
 * `status` alone is not enough. Deliver only moves `dispense_stage`; the status
 * stays RESERVED until the ward confirms, which can be hours later. Reading that
 * as "in flight" meant a handed-over transaction went on suppressing the order's
 * Fill-remaining button, so a part-filled order could not be topped up until the
 * nurse got round to confirming. Once it reaches DELIVERY the pharmacy has no
 * action left on it — the remaining work belongs to a NEW transaction.
 */
export function isOpenDispense(dispense) {
  const status = String(dispense?.status || '').toUpperCase();
  if (!status) return false;
  if (CLOSED_DISPENSE_STATUSES.includes(status)) return false;
  return String(dispense?.dispense_stage || '').toUpperCase() !== 'DELIVERY';
}

/** Newest-first ordering key. Falls back to the date-only field when created_at is absent. */
function dispenseSortKey(dispense) {
  return String(dispense?.created_at || dispense?.transaction_date || '');
}

/**
 * order_no -> the single open dispense to resume.
 *
 * An order can own several. "Continue Dispense" used to reopen the *start* page,
 * which created a brand-new transaction (and a brand-new controlled-auth request)
 * every pass — so a pharmacist who round-tripped through the Controlled Queue left a
 * trail of abandoned transactions. The newest open one is the live attempt; the rest
 * are the earlier laps.
 */
export function openDispensesByOrder(list) {
  const byOrder = new Map();
  (Array.isArray(list) ? list : []).forEach((dispense) => {
    const orderNo = dispenseOrderNo(dispense);
    if (!orderNo || !isOpenDispense(dispense)) return;
    const current = byOrder.get(orderNo);
    if (!current || dispenseSortKey(dispense) >= dispenseSortKey(current)) {
      byOrder.set(orderNo, dispense);
    }
  });
  return byOrder;
}

/**
 * The dispense list with the loop's duplicates collapsed away.
 *
 * Only *open* transactions that share an order are collapsed, and only down to the
 * newest — a closed one is a real record of work that happened, and a row with no
 * resolvable order (direct dispenses, walk-ins) is not a duplicate of anything.
 *
 * Nothing is deleted: the hidden transactions still exist and are still reachable at
 * /pharmacy/dispenses/<id>. This only stops the list repeating one order N times.
 */
export function dedupeDispensesByOrder(list) {
  const rows = Array.isArray(list) ? list : [];
  const keepByOrder = openDispensesByOrder(rows);
  const keptIds = new Set();
  keepByOrder.forEach((dispense) => keptIds.add(String(dispenseId(dispense))));

  return rows.filter((dispense) => {
    const orderNo = dispenseOrderNo(dispense);
    if (!orderNo || !isOpenDispense(dispense)) return true;
    return keptIds.has(String(dispenseId(dispense)));
  });
}

/** How many rows dedupeDispensesByOrder would hide — for an honest "N hidden" note. */
export function duplicateDispenseCount(list) {
  const rows = Array.isArray(list) ? list : [];
  return rows.length - dedupeDispensesByOrder(rows).length;
}

// ─── What an order has already had dispensed ────────────────────────────────

/**
 * Void — the units never left, so they are still owed. Deliberately NOT the same
 * list as CLOSED_DISPENSE_STATUSES: a DISPENSED transaction is closed *because*
 * the stock went out, and is exactly what has to be counted.
 */
const VOID_DISPENSE_STATUSES = ['CANCELLED', 'REVERSED'];

export function isVoidDispense(dispense) {
  return VOID_DISPENSE_STATUSES.includes(String(dispense?.status || '').toUpperCase());
}

function toNum(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

/**
 * EMR-only per-line link, stamped into the dispense line's `item_payload` at
 * create (buildCreateDispenseBody) and echoed back untouched by GET /items.
 *
 * It exists because nothing else points a dispense LINE at the order row it
 * fills: `source_reference_id` takes a UUID and IPD order ids are integers, and
 * the gateway's link table (PrescriptionDispenseLink) is not being written while
 * the order-fulfilment hooks are reverted. Matching on item_id alone is not a
 * substitute — the same medicine can sit on two orders of one admission.
 */
export function buildOrderLinkPayload(prescriptionId, orderNo) {
  const id = String(prescriptionId ?? '').trim();
  if (!/^\d+$/.test(id)) return null;
  const payload = { prescription_id: Number(id) };
  if (orderNo) payload.order_no = String(orderNo);
  return payload;
}

export function orderLinkFromItem(item) {
  const payload = item?.item_payload;
  if (!payload || typeof payload !== 'object') return null;
  const rxId = payload.prescription_id;
  if (rxId == null || rxId === '') return null;
  return { prescriptionId: String(rxId), orderNo: String(payload.order_no || '') };
}

/**
 * Units this dispense line has taken out of the pharmacy on the order's behalf.
 *
 * Counted from RESERVE, not from delivery: reserving already commits the batch in
 * stock-ledger, so offering the same units again would double-commit them even
 * though nothing has physically moved yet.
 *
 * `rejected_quantity` is subtracted because the ward refusing units receipts them
 * straight back into the same store (dispensing_service._finalize_staged_confirm)
 * — those are owed again. `returned_quantity` is NOT: a ward return is a separate
 * pharmacy ledger, not the order becoming unfilled.
 */
export function dispensedUnitsForOrder(item) {
  return Math.max(0, toNum(item?.requested_quantity) - toNum(item?.rejected_quantity));
}

function addToBucket(map, key, item) {
  const bucket = map.get(key) || { qty: 0, uomIds: [] };
  bucket.qty += dispensedUnitsForOrder(item);
  const uomId = item?.uom_id ? String(item.uom_id) : '';
  if (uomId && !bucket.uomIds.includes(uomId)) bucket.uomIds.push(uomId);
  map.set(key, bucket);
}

/**
 * Roll every dispense already raised against one order up into per-line totals.
 *
 * `entries` is [{ dispense, items }] — one per transaction belonging to the order,
 * paired with its GET /items rows.
 *
 * A line lands under its prescription-line id when the link is there, and under
 * its item_id otherwise (transactions raised before the link was stamped). Never
 * both, so a mixed set of old and new transactions cannot double-count.
 */
export function summarizeOrderDispenses(entries) {
  const byLineId = new Map();
  const byItemId = new Map();
  const transactions = [];

  (Array.isArray(entries) ? entries : []).forEach(({ dispense, items }) => {
    if (isVoidDispense(dispense)) return;
    const rows = Array.isArray(items) ? items : [];
    transactions.push({
      id: dispenseId(dispense),
      transaction_number: dispense?.transaction_number || '',
      status: dispense?.status || '',
      dispense_stage: dispense?.dispense_stage || '',
      created_at: dispense?.created_at || dispense?.transaction_date || '',
      qty: rows.reduce((sum, row) => sum + dispensedUnitsForOrder(row), 0),
      line_count: rows.length,
    });

    rows.forEach((item) => {
      const link = orderLinkFromItem(item);
      if (link) addToBucket(byLineId, link.prescriptionId, item);
      else if (item?.item_id) addToBucket(byItemId, String(item.item_id), item);
    });
  });

  transactions.sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  return { byLineId, byItemId, transactions };
}

/** What has already gone out for one prescription line, or null. */
export function priorDispenseForLine(ledger, line) {
  if (!ledger || !line) return null;
  const byLine = ledger.byLineId?.get?.(String(line.id));
  if (byLine) return byLine;
  return line.item_id ? (ledger.byItemId?.get?.(String(line.item_id)) || null) : null;
}

/**
 * The earlier dispense was counted in a different unit than the one the
 * pharmacist is typing in now, so its quantity cannot be subtracted from this
 * line — 2 BOX and 20 TAB are not comparable numbers. Reported rather than
 * guessed at (same "unknown, never a guess" rule as prescriptionFulfilment).
 */
export function priorDispenseUomMismatch(prior, uomId) {
  if (!prior?.uomIds?.length || !uomId) return false;
  return !prior.uomIds.includes(String(uomId));
}
